import { useEffect, useState, Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { PageLoader } from './components/PageLoader';
import DynamicPage from './components/DynamicPage';
import { moduleRoutes } from './modules/registry';
import { useModuleEnabled } from './hooks/usePublicConfig';

// Core 404 (not a module) — lazy so it costs nothing on the happy path.
const NotFound = lazy(() => import('./pages/NotFound'));
const ModuleUnavailable = lazy(() => import('./components/ModuleUnavailable'));

// Build-time parked mode (VITE_PARKED). The literal flag lets Rollup drop this
// chunk from normal builds and the site tree from parked builds.
const ParkedPage = __PARKED__ ? lazy(() => import('./pages/ParkedPage')) : null;

/** Tier-2 runtime gate for public module routes. Optimistic while config loads;
 *  the admin module is never gated (lockout prevention). */
function RuntimeModuleGate({ moduleId, children }: { moduleId: string; children: JSX.Element }) {
  const enabled = useModuleEnabled(moduleId);
  if (!enabled) return <ModuleUnavailable />;
  return children;
}

/**
 * Main Application Component
 *
 * Renders the parked page for every path when built with VITE_PARKED=true;
 * otherwise the full site. Kept as a separate component so SiteApp's hook
 * order never depends on the flag.
 */
export const App = () => (__PARKED__ ? <ParkedShell /> : <SiteApp />);

/** Parked mode: no Header, Footer, PageLoader or Routes. */
function ParkedShell() {
  const Page = ParkedPage as NonNullable<typeof ParkedPage>; // non-null whenever __PARKED__
  return (
    <Suspense fallback={<div className="min-h-[100svh] bg-background-dark" />}>
      <Page />
    </Suspense>
  );
}

/**
 * The full site: core routing, header, and footer.
 * Core routes are '/' and '/:slug' (the WordPress page engine); every other
 * route is composed from the module registry (see src/modules/README.md).
 *
 * @returns {JSX.Element} The main app component with routing configuration
 */
function SiteApp() {
  // Application initialization state
  const [isLoading, setIsLoading] = useState(true);
  const { pathname, hash } = useLocation();
  const isAdminRoute = pathname === '/admin' || pathname.startsWith('/admin/');

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  // Show loading spinner during app initialization
  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-container">
      {/* Site header with navigation (hidden on the admin dashboard) */}
      {!isAdminRoute && <Header />}

      {/* Main content area with routing */}
      <main className="flex-grow" data-testid="main-content">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Core: WordPress page engine */}
            <Route path="/" element={<DynamicPage />} />

            {/* Module routes — rendered BEFORE the /:slug WP catch-all (which
                would otherwise swallow single-segment paths like /admin).
                v6 ranking also guarantees static segments outrank /:slug. */}
            {moduleRoutes.map(({ path, moduleId, Component }) => (
              <Route
                key={path}
                path={path}
                element={
                  <RuntimeModuleGate moduleId={moduleId}>
                    <Component />
                  </RuntimeModuleGate>
                }
              />
            ))}

            {/* Core: WP catch-all for single-segment slugs */}
            <Route path="/:slug" element={<DynamicPage />} />

            {/* Multi-segment unknowns (/:slug can't match these) */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      {/* Site footer (hidden on the admin dashboard) */}
      {!isAdminRoute && <Footer />}
    </div>
  );
}
